import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { Loader2 } from 'lucide-react';

// Layouts
import DashboardLayout from './layouts/DashboardLayout';

// Lazy-loaded pages
const LandingPage = lazy(() => import('./pages/LandingPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const PlanSelectionPage = lazy(() => import('./pages/PlanSelectionPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const EmailConfirmedPage = lazy(() => import('./pages/EmailConfirmedPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ChangePasswordPage = lazy(() => import('./pages/ChangePasswordPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));

const EntrarTurmaPage = lazy(() => import('./pages/public/EntrarTurmaPage'));
const TermsPage = lazy(() => import('./pages/public/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/public/PrivacyPage'));
const LGPDPage = lazy(() => import('./pages/public/LGPDPage'));
const HelpCenterPage = lazy(() => import('./pages/public/HelpCenterPage'));
const ContactPage = lazy(() => import('./pages/public/ContactPage'));
const FAQPage = lazy(() => import('./pages/public/FAQPage'));

// Dashboard pages
const DashboardHome = lazy(() => import('./pages/dashboard/DashboardHome'));
const TurmasPage = lazy(() => import('./pages/dashboard/turmas/TurmasPage'));
const TurmaDetailPage = lazy(() => import('./pages/dashboard/turmas/TurmaDetailPage'));
const TurmaEditPage = lazy(() => import('./pages/dashboard/turmas/TurmaEditPage'));
const ProvasPage = lazy(() => import('./pages/dashboard/provas/ProvasPage'));
const ProvaDetailPage = lazy(() => import('./pages/dashboard/provas/ProvaDetailPage'));
const ProvaViewPage = lazy(() => import('./pages/dashboard/provas/ProvaViewPage'));
const NovaProvaPage = lazy(() => import('./pages/dashboard/provas/NovaProvaPage'));
const PlanosDeAulaPage = lazy(() => import('./pages/dashboard/planos-de-aula/PlanosDeAulaPage'));
const PlanoDeAulaDetailPage = lazy(() => import('./pages/dashboard/planos-de-aula/PlanoDeAulaDetailPage'));
const PlanoDeAulaEditPage = lazy(() => import('./pages/dashboard/planos-de-aula/PlanoDeAulaEditPage'));
const NovoPlanoDeAulaPage = lazy(() => import('./pages/dashboard/planos-de-aula/NovoPlanoDeAulaPage'));
const RelatoriosPage = lazy(() => import('./pages/dashboard/RelatoriosPage'));
const AssinaturaPage = lazy(() => import('./pages/dashboard/AssinaturaPage'));
const ConfiguracoesPage = lazy(() => import('./pages/dashboard/configuracoes/ConfiguracoesPage'));
const ConfiguracoesIAPage = lazy(() => import('./pages/dashboard/configuracoes/ConfiguracoesIAPage'));
const AnalyticsPage = lazy(() => import('./pages/dashboard/AnalyticsPage'));
const HistoricoPage = lazy(() => import('./pages/dashboard/HistoricoPage'));
const NotificacoesPage = lazy(() => import('./pages/dashboard/NotificacoesPage'));

// Admin pages
const AdminPage = lazy(() => import('./pages/dashboard/admin/AdminPage'));
const AdminUsersPage = lazy(() => import('./pages/dashboard/admin/AdminUsersPage'));
const AdminLogsPage = lazy(() => import('./pages/dashboard/admin/AdminLogsPage'));
const AdminFinancePage = lazy(() => import('./pages/dashboard/admin/AdminFinancePage'));
const AdminPlansPage = lazy(() => import('./pages/dashboard/admin/AdminPlansPage'));
const AdminLLMPage = lazy(() => import('./pages/dashboard/admin/AdminLLMPage'));
const AdminMercadoPagoPage = lazy(() => import('./pages/dashboard/admin/AdminMercadoPagoPage'));
const AdminMonitoringPage = lazy(() => import('./pages/dashboard/admin/AdminMonitoringPage'));
const AdminNotificationsPage = lazy(() => import('./pages/dashboard/admin/AdminNotificationsPage'));
const AdminEmailsPage = lazy(() => import('./pages/dashboard/admin/AdminEmailsPage'));
const NovoEmailPage = lazy(() => import('./pages/dashboard/admin/NovoEmailPage'));

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <Loader2 className="w-10 h-10 animate-spin text-green-500 mx-auto mb-4" />
        <p className="text-gray-500 font-medium">Carregando...</p>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/plans" element={<PlanSelectionPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/email-confirmed" element={<EmailConfirmedPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/change-password" element={<ChangePasswordPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/entrar-turma/:id" element={<EntrarTurmaPage />} />
        
        {/* Public info pages */}
        <Route path="/termos-de-uso" element={<TermsPage />} />
        <Route path="/privacidade" element={<PrivacyPage />} />
        <Route path="/lgpd" element={<LGPDPage />} />
        <Route path="/central-de-ajuda" element={<HelpCenterPage />} />
        <Route path="/contato" element={<ContactPage />} />
        <Route path="/faq" element={<FAQPage />} />

        {/* Dashboard routes */}
        <Route path="/dashboard" element={<DashboardLayout />}>
          <Route index element={<DashboardHome />} />

          {/* Turmas */}
          <Route path="turmas" element={<TurmasPage />} />
          <Route path="turmas/:id" element={<TurmaDetailPage />} />
          <Route path="turmas/:id/editar" element={<TurmaEditPage />} />

          {/* Provas */}
          <Route path="provas" element={<ProvasPage />} />
          <Route path="provas/nova" element={<NovaProvaPage />} />
          <Route path="provas/:id" element={<ProvaDetailPage />} />
          <Route path="provas/:id/prova" element={<ProvaViewPage />} />

          {/* Planos de Aula */}
          <Route path="planos-de-aula" element={<PlanosDeAulaPage />} />
          <Route path="planos-de-aula/novo" element={<NovoPlanoDeAulaPage />} />
          <Route path="planos-de-aula/:id" element={<PlanoDeAulaDetailPage />} />
          <Route path="planos-de-aula/:id/editar" element={<PlanoDeAulaEditPage />} />

          {/* Other */}
          <Route path="relatorios" element={<RelatoriosPage />} />
          <Route path="assinatura" element={<AssinaturaPage />} />
          <Route path="configuracoes" element={<ConfiguracoesPage />} />
          <Route path="configuracoes/ia" element={<ConfiguracoesIAPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="historico" element={<HistoricoPage />} />
          <Route path="notificacoes" element={<NotificacoesPage />} />

          {/* Admin */}
          <Route path="admin" element={<AdminPage />} />
          <Route path="admin/users" element={<AdminUsersPage />} />
          <Route path="admin/logs" element={<AdminLogsPage />} />
          <Route path="admin/finance" element={<AdminFinancePage />} />
          <Route path="admin/plans" element={<AdminPlansPage />} />
          <Route path="admin/llm" element={<AdminLLMPage />} />
          <Route path="admin/mercadopago" element={<AdminMercadoPagoPage />} />
          <Route path="admin/monitoring" element={<AdminMonitoringPage />} />
          <Route path="admin/notifications" element={<AdminNotificationsPage />} />
          <Route path="admin/emails" element={<AdminEmailsPage />} />
          <Route path="admin/emails/novo" element={<NovoEmailPage />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
